import React, { Fragment, useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Dialog, Transition } from '@headlessui/react';
import submissionBoardService from "../../services/SubmissionBoardService";
import { showToast } from "../../shared/Toast";
import { SbCurrentUser, SbRecipientModel } from "./SbModel";
import Button from "./Button";
import { XIcon } from "lucide-react";
import Image from "../../Image";

interface ShortListProfileNotesProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: SbCurrentUser;
  sb_recipient?: SbRecipientModel;
  projectId: string;
  submissionBoardId: number;
  isSbLocked: boolean;
  profileId: number;
  profileName: string;
  profileImage: string;
  submissionBoardName: string;
  defaultUserAvatar: string;
  onNoteAdded: (note: string) => void;
}

const SbAddNote: React.FC<ShortListProfileNotesProps> = ({
  isOpen,
  onClose,
  currentUser,
  sb_recipient,
  projectId,
  submissionBoardId,
  isSbLocked,
  profileId,
  profileName,
  profileImage,
  submissionBoardName,
   defaultUserAvatar,
  onNoteAdded,
}) => {
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSubmissionBoard, setSelectedSubmissionBoard] = useState(null);
  const [note, setNote] = useState('');

  useEffect(() => {
    // Access the input element directly using its stable ID
    const emailElement = document.getElementById('add_note');
    if (emailElement) {
      // Assert that the element is an HTMLInputElement before calling focus
      (emailElement as HTMLInputElement).focus();
    }
  }, []);
  useEffect(() => {
    if (
      submissionBoardId == undefined ||
      submissionBoardId == null ||
      projectId == undefined ||
      projectId == null
    ) {
      return;
    }
    setSelectedSubmissionBoard(submissionBoardId);
    setSelectedProject(projectId);
    handleShowDialog();
  }, [submissionBoardId]);

  const handleShowDialog = () => {
    setShowDialog(true);
  };
  const handleHideDialog = () => {
    setSelectedSubmissionBoard(null);
    // if (dialogCloseHandler) {
    //   dialogCloseHandler(false);
    // }
    setShowDialog(false);
  };
  const saveClickHandler = async () => {
    if (!note.trim()) {
      showToast('Note cannot be empty', 'danger');
      return;
    }

    try {
      if (
        selectedProject == undefined ||
        selectedProject == null ||
        selectedSubmissionBoard == undefined ||
        selectedSubmissionBoard == null
      ) {
        throw new Error('Error: Invalid data selection.');
      }
      setLoading(true);
      await submissionBoardService.addProfileNote(
        projectId,
        submissionBoardId,
        profileId,
        note,
        sb_recipient && sb_recipient.key_token ? sb_recipient.key_token : '',
        sb_recipient && sb_recipient.otp_valid ? sb_recipient.otp_valid : ''
      );
      showToast('Note added successfully', 'success');
      onNoteAdded(note);
      setNote('');
      onClose();
    } catch (error) {
      showToast('Failed to add note', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const onCloseDialog = () => {
    setNote('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative " style={{ zIndex: 55 }} onClose={onCloseDialog}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-customGray" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative flex flex-col absolute  rotate-0 opacity-100   border border-solid border-gray-300  bg-white py-1  rounded-md shadow-xl-0-19-40-8">
                  {/* <div className="flex items-center justify-between px-4 py-2">
                    <h2 className="text-lg font-medium text-gray-900">Add Note</h2>
                    <button
                      id={'addnote_close_model_id'}
                      type="button"
                      className="flex h-8 w-8 items-center justify-center rounded-md "
                      onClick={handleCancel}
                    >
                      <span className="sr-only">Close menu</span>
                      <XMarkIcon className="h-8 w-8" aria-hidden="true" />
                    </button>
                  </div> */}

                  <div className="justify-items-center text-left">
                    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                      <div
                        className="bg-white rounded-lg shadow-xl flex flex-col overflow-visible gap-2-1"
                        style={{
                          height: '80vh',
                          maxHeight: '31.25rem',
                          width: '80vw',
                          maxWidth: '31.25rem',
                        }}
                      >
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 sm:px-6 py-5">
                          <h2 className="text-lg font-medium text-black">Add Note</h2>
                          <button
                            className="flex items-center justify-center text-gray-700 hover:text-gray-600 transition-colors h-6 w-6"
                            onClick={onClose}
                          >
                            <XIcon className="w-6 h-6" />
                          </button>
                        </div>

                        {/* Content */}
                        <div
                          className="pb-10 px-4 sm:px-10 scrollable-content overflow-y-scroll"
                          style={{}}
                        >
                          <div className="gap-5 flex flex-col">
                            {/* Profile Info */}
                            <div className="flex flex-row gap-2-1 items-center ">
                              <div
                                className="rounded-lg-10"
                                style={{
                                  width: '3.125rem',
                                  height: '3.125rem',
                                }}
                              >
                                <Image
                                  src={profileImage || defaultUserAvatar}
                                  fallbackSrc={defaultUserAvatar}
                                  alt={profileName}
                                  className="w-full h-full object-cover rounded-lg-10"
                                />
                              </div>
                              <div className="">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                  {profileName}
                                </h3>
                                <p className="text-gray-600 text-sm">{submissionBoardName}</p>
                              </div>
                            </div>
                            <hr />
                            {/* Add Note Section */}
                            <div className="flex flex-col gap-1">
                              <label className="block text-sm font-medium text-gray-700">
                                Add note to Profile (not viewable to talent)
                              </label>
                              <textarea
                                id="add_note"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="Enter a note for this profile..."
                                className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                                rows={4}
                              />
                            </div>
                            {/* Footer */}
                            <div className="flex items-center justify-center gap-2-1 pt-2">
                              <Button
                                id="cancel_add_note_button"
                                variant="secondary"
                                onClick={onCloseDialog}
                              // className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                              >
                                Cancel
                              </Button>
                              <Button
                                id="save_add_note_button"
                                variant="primary"
                                onClick={saveClickHandler}
                                disabled={loading || !note.trim()}
                              // className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {loading ? 'Saving...' : 'Submit'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default SbAddNote;